import {AsyncPipe, NgClass, NgFor, NgIf} from '@angular/common';
import {Component, DestroyRef, OnDestroy, OnInit, inject} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {FormControl, ReactiveFormsModule} from '@angular/forms';
import {MatButtonModule} from '@angular/material/button';
import {MatCheckboxModule} from '@angular/material/checkbox';
import {MatDialog} from '@angular/material/dialog';
import {MatDividerModule} from '@angular/material/divider';
import {MatIconModule} from '@angular/material/icon';
import {MatMenuModule} from '@angular/material/menu';
import {MatTooltipModule} from '@angular/material/tooltip';
import {MatToolbarModule} from '@angular/material/toolbar';
import {BehaviorSubject, Observable, Subject, combineLatest} from 'rxjs';
import {
  distinctUntilChanged,
  filter,
  map,
  startWith,
  switchMap,
  tap,
  withLatestFrom
} from 'rxjs/operators';
import {TranslateModule} from '@ngx-translate/core';
import {
  IconButtonComponent,
  PrimaryButtonComponent,
  SecondaryButtonComponent
} from '@q9elements/ui-kit/components';
import {SignOffTaskDialogComponent, SignOffTaskPayload} from '@q9elements/ui-kit/maps';
import {
  Q9HtmlEncoderPipe,
  Q9TrackDirective,
  SnackBarService,
  TASK_TYPES,
  ThemePalette
} from '@q9elements/ui-kit/common';
import {NavigationService} from '../../../core/navigation/navigation.service';
import {
  ITrainingItem,
  ITrainingTask,
  TRAINING_TASK_STATUSES_ENUM,
  TrainingsService
} from '../../../core/services/trainings.service';
import {DiagramJsonHandlerService} from '../../../diagram-data/diagram-json-handler.service';
import {DIAGRAM_VERSION_STATES_ENUM} from '../../shared/models/diagram-version.constant';
import {TrainingPopupComponent} from '../../shared/training-popup/training-popup.component';

const trainingStates = {
  seen: 'seen',
  unSeen: 'unSeen',
  done: 'done',
  cancelled: 'cancelled'
};

interface MarkDiagramInterface {
  lastTrainingItem: boolean;
  numberOfDiagrams: number;
}

@Component({
  selector: 'app-training-navigation',
  templateUrl: './training-navigation.component.html',
  styleUrls: ['./training-navigation.component.scss'],
  standalone: true,
  imports: [
    NgIf,
    NgClass,
    MatMenuModule,
    MatCheckboxModule,
    MatTooltipModule,
    MatButtonModule,
    MatToolbarModule,
    MatIconModule,
    TrainingPopupComponent,
    NgFor,
    MatDividerModule,
    AsyncPipe,
    TranslateModule,
    ReactiveFormsModule,
    Q9TrackDirective,
    IconButtonComponent,
    PrimaryButtonComponent,
    SecondaryButtonComponent,
    Q9HtmlEncoderPipe
  ]
})
export class TrainingNavigationComponent implements OnInit, OnDestroy {
  public confirmCheckedControl = new FormControl(false);
  private destroyRef = inject(DestroyRef);

  private lastVisited: ITrainingItem;

  private initialOutsideBound$ = new Subject<boolean>();

  public enabled$ = this.trainingSvc.trainingMode$;

  private currTaskSubject: BehaviorSubject<ITrainingTask> = new BehaviorSubject(undefined);

  private tooltipSubject = new BehaviorSubject<ITrainingTask>(undefined);

  public currentTaskStatus$ = this.currTaskSubject.pipe(
    filter(currentTask => !!currentTask),
    map(currentTask => currentTask.status)
  );

  public navArray$: Observable<Array<ITrainingItem>> = this.currTaskSubject.pipe(
    filter(x => Boolean(x)),
    map(data => data.trainingItems)
  );
  private currDv$ = this.diagramJsonSvc.diagramJson$.pipe(
    filter(x => Boolean(x.diagramId)),
    distinctUntilChanged((dvA, dvB) => dvA.id === dvB.id)
  );

  public current$: Observable<ITrainingItem> = combineLatest([this.navArray$, this.currDv$]).pipe(
    filter(([x, y]) => Boolean(x) && Boolean(y)),
    map(([arr, currDv]) => this.findTrainingItem(arr, currDv.diagramId)),
    tap(currentTraining => this.setupInitialBounds(currentTraining)),
    filter(currentTraining => Boolean(currentTraining)),
    tap(curr => {
      this.setNavKeyAvailability(curr);
      this.setLastVisited(curr);
    })
  );

  public currentPosition$ = this.current$.pipe(
    map(curr => {
      const arr = this.currTaskSubject.value.trainingItems;
      const index = arr.indexOf(curr);

      return index + 1;
    })
  );

  public checked$: Observable<boolean> = this.current$.pipe(
    map(item => item.status === trainingStates.seen || item.status === trainingStates.done)
  );

  public outOfBounds$: Observable<boolean> = combineLatest(
    this.currDv$,
    this.initialOutsideBound$
  ).pipe(
    map(([dv, shadowOutOfBound]) => {
      if (shadowOutOfBound) {
        return true;
      }

      if (dv.state !== DIAGRAM_VERSION_STATES_ENUM.MASTER) {
        return true;
      }

      if (!this.currTaskSubject.value) {
        return;
      }

      const arr = this.currTaskSubject.value.trainingItems;
      const item = this.findTrainingItem(arr, dv.diagramId);

      return !item; // return true if there is no item
    }),
    startWith(false)
  );

  public tooltip$ = combineLatest([this.tooltipSubject, this.outOfBounds$]).pipe(
    map(([tooltip, outOfBounds]) => ({tooltip, outOfBounds}))
  );

  public diagramHistory$ = this.diagramJsonSvc.diagramJson$.pipe(
    takeUntilDestroyed(this.destroyRef),
    filter(diagram => Boolean(diagram) && !!Object.keys(diagram).length),
    filter(diagram => diagram && diagram.history),
    map(diagramJson => diagramJson.history.find(record => record.what === 'ready'))
  );
  public taskStatusChecked$ = combineLatest([this.currentTaskStatus$, this.checked$]).pipe(
    map(([status, checked]) => {
      return {
        isCompleted: status === this.taskStatuses.COMPLETED,
        isChecked: checked
      };
    })
  );

  public hasNext: boolean;
  public hasPrev: boolean;

  protected readonly taskStatuses = TRAINING_TASK_STATUSES_ENUM;

  constructor(
    private trainingSvc: TrainingsService,
    private diagramJsonSvc: DiagramJsonHandlerService,
    private navigationSvc: NavigationService,
    private snackBarSvc: SnackBarService,
    private dialog: MatDialog
  ) {}

  ngOnInit() {
    this.initObservables();
  }

  ngOnDestroy() {
    this.tooltipSubject.complete();
    this.initialOutsideBound$.complete();
  }

  setupInitialBounds(training) {
    if (!training) {
      return this.initialOutsideBound$.next(true);
    }

    return this.initialOutsideBound$.next(false);
  }

  public closeTask(): void {
    this.trainingSvc.wipeTraining();
    const {diagramId} = this.diagramJsonSvc.get();

    this.navigationSvc.relative(diagramId);
  }

  private signOffTask(taskPayload: SignOffTaskPayload) {
    return this.dialog
      .open(SignOffTaskDialogComponent, {
        width: '400px',
        panelClass: ['q9-dialog'],
        data: {
          color: ThemePalette.PRIMARY,
          taskPayload,
          isDiagramEditor: true
        }
      })
      .afterClosed()
      .subscribe((signOffed: boolean) => {
        if (signOffed) {
          this.updateTraining('done');
          this.snackBarSvc.success('TOOLBAR.TRAINING_COMPLETED_SNACKBAR');
        } else {
          this.updateTraining('seen');
        }

        this.closeTask();
      });
  }

  private updateAllTrainingItems(currTraining, status: 'done' | 'seen' | 'unSeen') {
    if (status === 'done') {
      currTraining.status = 'completed';
    }

    currTraining.trainingItems = currTraining.trainingItems.map(trItem => {
      trItem.status = status;

      return trItem;
    });

    return this.currTaskSubject.next(currTraining);
  }

  private updateOneTrainingItem(
    currTraining,
    status: 'done' | 'seen' | 'unSeen',
    diagramId: string
  ) {
    currTraining.trainingItems = currTraining.trainingItems.map(trItem => {
      if (trItem.item.diagramId === diagramId) {
        trItem.status = status;
      }

      return trItem;
    });

    this.currTaskSubject.next(currTraining);
  }

  private updateTraining(status: 'done' | 'seen' | 'unSeen', diagramId?: string) {
    const currTraining = this.currTaskSubject.value;

    if (diagramId) {
      return this.updateOneTrainingItem(currTraining, status, diagramId);
    }

    return this.updateAllTrainingItems(currTraining, status);
  }

  private setLastVisited(curr) {
    this.lastVisited = curr;
  }

  private setNavKeyAvailability(curr: ITrainingItem) {
    const arr = this.currTaskSubject.value.trainingItems;
    const index = arr.indexOf(curr);

    this.hasNext = Boolean(arr[index + 1]);
    this.hasPrev = Boolean(arr[index - 1]);
  }

  private findCurrentDiagram(arr: Array<ITrainingItem>) {
    const diagramId = this.diagramJsonSvc.get().diagramId;

    return this.findTrainingItem(arr, diagramId);
  }

  private findCurrentDiagramIndex(arr: Array<ITrainingItem>) {
    return arr.indexOf(this.findCurrentDiagram(arr));
  }

  public markDiagram({checked}) {
    const diagramId = this.diagramJsonSvc.get().diagramId;
    const connectionId = this.trainingSvc.getCurrentId();
    const params = {diagramId, connectionId, checked};

    return this.trainingSvc.markDiagram(params).subscribe({
      next: (response: MarkDiagramInterface) => {
        const {lastTrainingItem = false, numberOfDiagrams = 0} = response;

        if (lastTrainingItem) {
          const taskPayload: SignOffTaskPayload = {
            type: TASK_TYPES.TRAINING,
            numberOfDiagrams,
            connectionId
          };

          return this.signOffTask(taskPayload);
        }

        if (checked) {
          this.updateTraining('seen', diagramId);

          return this.navNext();
        }

        return this.updateTraining('unSeen', diagramId);
      },
      error: ({error: response}) => {
        this.snackBarSvc.error(response.error);
      }
    });
  }

  private initObservables() {
    this.diagramJsonSvc.diagramJson$
      .pipe(
        filter(json => !!json && json.diagramId),
        withLatestFrom(this.trainingSvc.currentRequestId$),
        filter(([x, y]) => Boolean(x) && Boolean(y)),
        map(([diagramJson, connectionId]) => {
          return [connectionId, diagramJson.diagramId];
        }),
        switchMap(([connectionId, diagramId]) =>
          this.trainingSvc.getTrainingTaskDiagramList(connectionId, diagramId)
        ),
        filter(training => !!training)
      )
      .subscribe(data => {
        if (data) {
          this.tooltipSubject.next(data);
        }

        this.currTaskSubject.next(data);
      });
  }

  public navNext() {
    const arr = this.currTaskSubject.value.trainingItems;
    const currIndex = this.findCurrentDiagramIndex(arr);
    const nextDiagram = arr[currIndex + 1];

    if (nextDiagram) {
      this.processNavigation(nextDiagram.item.diagramId);
    }
  }

  public navPrev() {
    const arr = this.currTaskSubject.value.trainingItems;
    const currIndex = this.findCurrentDiagramIndex(arr);
    const prevDiagram = arr[currIndex - 1];

    if (prevDiagram) {
      this.processNavigation(prevDiagram.item.diagramId);
    }
  }

  public processNavigation(id) {
    this.navigationSvc.relative(id);
  }

  public navBackToLastTrainingItem() {
    const arr = this.currTaskSubject.value.trainingItems;
    const navTo = this.lastVisited ? this.lastVisited.item.diagramId : arr[0].item.diagramId;

    // todo: change this if you need to navigate trainings on archive versions
    this.navigationSvc.navigate(navTo, DIAGRAM_VERSION_STATES_ENUM.MASTER);
  }

  private findTrainingItem(arr: Array<ITrainingItem>, diagramId: string) {
    return arr.find(data => data.item.diagramId === diagramId && data.type === 'diagram');
  }
}
